import React, { useEffect, useMemo, useRef, useState } from "react";
import { OrderFilter } from "../components";
import { MainLayout } from "../layouts/MainLayout";
import { useDispatch, useSelector } from "react-redux";
import { yodaAgentActions, yodaOrdersActions } from "../logic/actions";
import { getLocalizedString } from "../utils";
import { useParams } from "react-router-dom";
import { Box, Flex } from "rebass";
import config from "../utils/globalConfig";
import { LoadingFrame } from "../components/common/LoadingFrame";
import useVirtualList from "../hooks/useVirtualList";
import { PreLoader } from "../components/loader/PreLoader";
import { resetScroll } from "../utils/resetScroll";
import MobileScrollCTA from "../components/MobileScrollCTA";
import useResponsive from "../hooks/useResponsive";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";
import OrderDraft from "../components/order/OrderDraft";
import { ORDER_STATUS } from "../model/order";

const AdminDraftOrdersPage = () => {
  const dispatch = useDispatch();
  const staticSectionRef = useRef();
  const [staticSectionHeight, setStaticSectionHeight] = useState(0);
  const { orders } = useSelector((state) => state.yodaOrders);
  const [ordersFiltered, setOrdersFiltered] = useState(orders);
  const { me } = useSelector((state) => state.yodaStakeholders);
  const { offsetTop } = useSelector((state) => state.system);

  const { scrolledToBottom } = useSelector((state) => state.layout);

  const { customerId } = useParams();
  const { isMobile } = useResponsive();
  const { getVirtualItems, reset } = useVirtualList({ enableAutoScroll: true });

  const [filters, setFilters] = useState({
    season: "-",
    typeId: "-",
    line: "-",
    status: "-",
  });

  const displayedOrders = useMemo(
    () => getVirtualItems(ordersFiltered),
    [getVirtualItems, ordersFiltered],
  );

  useEffect(() => {
    setOrdersFiltered(orders);
  }, [orders]);

  useEffect(() => {
    let tmpOrders = orders
      .filter((order) => {
        if (filters.status === ORDER_STATUS.CANCELLED) {
          return (
            order?.recycled ||
            order?.status === ORDER_STATUS.CANCELLED ||
            (order.status === 0 && order.statusDescription === "Cancelled")
          );
        } else if (filters.status === ORDER_STATUS.DRAFT) {
          return (
            order.status === ORDER_STATUS.DRAFT &&
            order.statusDescription !== "Cancelled"
          );
        } else if (filters.status === ORDER_STATUS.SUBMITTED) {
          return (
            order.status === ORDER_STATUS.SUBMITTED &&
            order.statusDescription !== "Cancelled"
          );
        } else if (filters.status === ORDER_STATUS.CONFIRMED) {
          return (
            order.status === ORDER_STATUS.CONFIRMED &&
            order.statusDescription !== "Cancelled"
          );
        }
        return true;
      })
      .filter((order) =>
        filters.typeId !== "all" && filters.typeId !== "-"
          ? order.typeId === filters.typeId
          : true,
      )
      .filter((order) =>
        filters.season !== "all" && filters.season !== "-"
          ? order.seasonId === filters.season
          : true,
      )
      .filter((order) =>
        filters.line !== "all" && filters.line !== "-"
          ? order.customData?.lineId === filters.line
          : true,
      );
    setOrdersFiltered(tmpOrders);
    reset();
    resetScroll();
  }, [filters]);

  useEffect(() => {
    setStaticSectionHeight(staticSectionRef?.current?.clientHeight);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [staticSectionRef?.current?.clientHeight]);

  useEffect(() => {
    reset();
    resetScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (customerId !== null && me !== null) {
      dispatch(yodaAgentActions.getAll());
      dispatch(yodaOrdersActions.getAllAdminDraft());
    }
  }, [me]);

  function handleResize() {
    setStaticSectionHeight(staticSectionRef?.current?.clientHeight);
  }

  if (
    me?.error === "not-authorized" ||
    (me?.role && me?.role !== "CustomerCare")
  ) {
    return (
      <Redirect
        to={{
          pathname: "/not-authorized",
          state: {
            yodaError: me?.error === "not-authorized",
            roleError: me?.role && me?.role !== "CustomerCare",
          },
        }}
      />
    );
  }
  return (
    <MainLayout>
      <LoadingFrame order={true}></LoadingFrame>
      <Box
        sx={{
          position: "sticky",
          top: [offsetTop, 0],
          zIndex: 1,
          background: "white",
          width: ["100%"],
          maxWidth: config.VIEWPORT,
          margin: "0 auto",
          padding: ["0 8px", 0],
        }}
        ref={staticSectionRef}
        // mb={["10px", 1, 1]}
      >
        <Flex
          width={1}
          justifyContent={"space-between"}
          flexDirection={["column", "column", "column"]}
          mt={20}
          mb={30}
        >
          <OrderFilter
            setFilters={setFilters}
            orders={orders}
            filters={filters}
          />
        </Flex>
        <Box
          sx={{ height: [0, 0, 15] }}
          style={{ backgroundColor: "white" }}
        ></Box>
      </Box>
      <Box>
        {displayedOrders?.map((order) => (
          <OrderDraft {...order} instance={order} key={order.orderNumber} />
        ))}
        {!!displayedOrders?.length &&
          displayedOrders?.length < ordersFiltered?.length &&
          scrolledToBottom && (
            <Flex
              justifyContent={"center"}
              alignContent={"center"}
              sx={{ width: "100%", py: "20px" }}
            >
              <PreLoader color="black" size="sm" />
            </Flex>
          )}
        {(!displayedOrders || displayedOrders.length === 0) && (
          <Flex
            style={{
              height: "30px",
              display: "grid",
              justifyContent: "center",
              alignContent: "center",
            }}
            justifyContent={"center"}
            mb={4}
            fontSize={12}
          >
            {getLocalizedString("ORDERS IS EMPTY")}
          </Flex>
        )}
        {isMobile &&
          !!displayedOrders?.length &&
          displayedOrders?.length < ordersFiltered?.length && (
            <MobileScrollCTA />
          )}
      </Box>
    </MainLayout>
  );
};

export { AdminDraftOrdersPage };
